import { z } from 'zod';
import { LOCATION_INTEL_PAGE_MAX, LOCATION_RECENCY_BANDS } from '../constants/locationIntel.constant';
import { objectIdSchema } from './common.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';
import { rankSummarySchema } from './rank.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  LOCATIONAL INTEL — THE WIRE FORMAT. [LOCATION-INTEL]
 *
 *  Three reads, all behind `LOCATION_INTEL_ACCESS` (`officer.read` AND the `location_intel` flag), all
 *  inside the caller's own scope:
 *
 *   · `GET /location-intel/points` — every officer in reach who has reported a location: an id, the
 *     NEWEST point and its recency band. ⚠️ NO NAME, NO RANK, NO PHONE, NO POSTING: a bulk list of the
 *     force's positions must not also be a directory sitting in a browser cache.
 *   · `GET /location-intel/officers/:officerId` — ONE officer, on a click: name, rank, unit, division,
 *     when they last reported, and the duty they are standing now if any. ⚠️ NEVER A PHONE, and so —
 *     the user's decision — no audit row is written: it is no wider than the officer list a holder can
 *     already open, plus the one point the map already drew. [amended LOCATION-INTEL.2] And the duty in
 *     progress: its places only when its order is in the caller's area; outside it, the date and the time
 *     and nothing more (`placesWithheld`, FR-3.6's redaction — never the shift's name).
 *   · `GET /location-intel/config` — the map's public token, to holders only.
 *
 *  ⚠️ [amended officerTrail.schema.ts' "no other schema carries a location"] THIS ONE DOES, BY DESIGN —
 *  one point per officer, the newest, never an address and never the rest of the trail.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const locationIntelPointsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(LOCATION_INTEL_PAGE_MAX).default(LOCATION_INTEL_PAGE_MAX),
});

/**
 * ⚠️ FIVE KEYS AND NO MORE — `locationIntel.test.ts` asserts the exact key set of every item. The id is
 * what a click sends back; everything that names the officer is the detail's, one officer at a time.
 */
export const locationIntelPointSchema = z.object({
  id: objectIdSchema,
  lng: z.number().min(-180).max(180),
  lat: z.number().min(-90).max(90),
  band: z.enum(LOCATION_RECENCY_BANDS),
  /** The SERVER's receipt time of the newest point — the instant the band was decided from. */
  recordedAt: z.iso.datetime(),
});

export const locationIntelOfficerParamsSchema = z.object({
  officerId: objectIdSchema,
});

/**
 * The duty an officer is standing NOW — `dutyInProgress`'s answer, the same one Duty Mitra's location
 * status reads. When several are in progress, the most recently started (the most specific).
 *
 * ⚠️ `placesWithheld` — FR-3.6. An officer inside the caller's scope can stand a duty on an order the
 * caller cannot see (a transfer after naming, a reparent). Then the date and time are shown and the
 * places are not: `conflictDeferred.test.ts`' written redaction rule, "on duty, 20:00–02:00, and nothing
 * more". The lists are empty and `reportingPlace` is null exactly when this is true.
 */
export const locationIntelDutySchema = z.object({
  /** The IST business date, `YYYY-MM-DD`. */
  dutyDate: z.string(),
  /** Date and time, humanised on the server — `Thu 15 Oct · 6:00 AM–2:00 PM`, `· Whole day`, `· From 3:00 PM`. */
  slotLabel: z.string(),
  /** The duty place, or a route's stops in order. */
  dutyPlaces: z.array(z.string()),
  reportingPlace: z.string().nullable(),
  placesWithheld: z.boolean(),
});

export const locationIntelOfficerSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  rank: rankSummarySchema.nullable(),
  /** The officer's posting. */
  unit: orgUnitSummarySchema.nullable(),
  /** The division above it — the unit itself when the officer is posted TO a division. */
  division: orgUnitSummarySchema.nullable(),
  lastReportedAt: z.iso.datetime(),
  band: z.enum(LOCATION_RECENCY_BANDS),
  /** ⚠️ `null` IS "NO DUTY IN PROGRESS", and the modal then says nothing about duty at all. */
  duty: locationIntelDutySchema.nullable(),
});

/**
 * ⚠️ A MAPBOX **PUBLIC** TOKEN (`pk.…`) OR NOTHING. The server reads the deployment's one Mapbox position —
 * the geocoding key — and hands it out only when it IS a public token, so a Google key or a Mapbox secret
 * (`sk.…`) never reaches a browser. `null` is "the map is not configured", and the page says so.
 */
export const locationIntelConfigSchema = z.object({
  mapboxAccessToken: z.string().nullable(),
});
